import Upload from "@mui/icons-material/Upload";
import { Box, Button, Stack, Typography } from "@mui/material";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  EditMode,
  defaultAddEditDocumentDialogState,
} from "components/Document";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { DocumentsDataGrid } from "../components/DocumentsDataGrid";
import { DocumentsPickerDialog } from "../components/DocumentsPickerDialog";
import { useCommunicationFormContext } from "../hooks/useCommunicationForm";

export const DocumentsStep = () => {
  const { control, watch, mode } = useCommunicationFormContext();

  const tenant = watch("tenant");
  const facilities = watch("facilities");

  const disabled = mode === "view";

  const [showDocumentPicker, setShowDocumentPicker] = useState(false);
  const [addEditDialogState, setAddEditDialogState] =
    useState<AddEditDocumentDialogState>(defaultAddEditDocumentDialogState);

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="subtitle1">Documents</Typography>
        <Typography variant="caption">
          Select document(s) to mail to all recipients. Choose existing
          documents from any associated facility or report, or upload a new
          file. Cover letters will be attached in the next step, once recipients
          are selected. You can drag and drop to reorder documents.
        </Typography>
      </Stack>
      <Box>
        <Controller
          name="communicationDocuments"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Stack gap={3}>
              <Stack direction="row" gap={3}>
                <Button
                  size="large"
                  variant="outlined"
                  disabled={disabled || !tenant}
                  onClick={() => setShowDocumentPicker(true)}
                >
                  Select Documents
                </Button>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Upload />}
                  disabled={disabled || !tenant}
                  onClick={() =>
                    setAddEditDialogState((state) => ({
                      ...state,
                      open: true,
                      mode: EditMode.ADD,
                    }))
                  }
                >
                  Upload New
                </Button>
              </Stack>

              {addEditDialogState.open && (
                <AddEditDocumentDialog
                  dialogState={addEditDialogState}
                  setDialogState={setAddEditDialogState}
                  tenantId={tenant?.id ?? ""}
                  showFacilityPicker
                  showTagPicker
                  onSubmit={(document) => {
                    onChange([...value, document]);
                    setAddEditDialogState(defaultAddEditDocumentDialogState);
                  }}
                />
              )}

              {showDocumentPicker && (
                <DocumentsPickerDialog
                  tenantId={tenant?.id ?? ""}
                  facilityIds={facilities.map((facility) => facility.id)}
                  initialDocumentIds={value.map((document) => document.id)}
                  close={() => setShowDocumentPicker(false)}
                  saveDocuments={(documents) => {
                    onChange(documents);
                    setShowDocumentPicker(false);
                  }}
                />
              )}

              {value.length > 0 && (
                <DocumentsDataGrid
                  documentIds={value.map((document) => document.id)}
                  removeDocument={
                    disabled
                      ? null
                      : (id) => {
                          onChange(value.filter((x) => x.id !== id));
                        }
                  }
                  onReorderDocuments={
                    disabled
                      ? undefined
                      : (newOrder) => {
                          // Reorder the documents based on the new order of IDs
                          const reorderedDocuments = newOrder.map((id) => {
                            const document = value.find((doc) => doc.id === id);
                            return document!; // We know it exists
                          });
                          onChange(reorderedDocuments);
                        }
                  }
                />
              )}

              <ErrorDisplay error={error} />
            </Stack>
          )}
        />
      </Box>
    </Stack>
  );
};
